import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, MessageCircle } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_cdbf81a7-7ac8-4363-9dd6-522b29aa8022/artifacts/lst2w5gp_WhatsApp%20Image%202026-01-24%20at%2017.19.09.jpeg"
                alt="Dharamshala Dalhousie Tour"
                className="h-20 md:h-24"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Your trusted local partner for unforgettable Himachal Pradesh journeys. Crafting memories since 2010.
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-xl flex items-center justify-center transition-all hover:scale-110">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-xl flex items-center justify-center transition-all hover:scale-110">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-red-600 rounded-xl flex items-center justify-center transition-all hover:scale-110">
                <Youtube className="h-5 w-5" />
              </a>
              <a 
                href="https://wa.me/918679333354" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-green-600 rounded-xl flex items-center justify-center transition-all hover:scale-110"
              >
                <MessageCircle className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white relative inline-block">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-green-500 to-orange-500 rounded-full"></span>
            </h3>
            <ul className="space-y-3 mt-4">
              <li>
                <Link to="/quote" className="text-green-400 hover:text-green-300 transition-colors text-sm font-bold flex items-center gap-2" data-testid="footer-quote-link">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                  Plan My Trip
                </Link>
              </li>
              <li>
                <Link to="/" className="text-gray-400 hover:text-green-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about-us" className="text-gray-400 hover:text-green-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/#packages" className="text-gray-400 hover:text-green-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  Tour Packages
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-400 hover:text-green-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  Travel Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-green-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Popular Tours */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white relative inline-block">
              Popular Tours
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-green-500 to-orange-500 rounded-full"></span>
            </h3>
            <ul className="space-y-3 mt-4">
              <li>
                <Link to="/packages/dharamshala-dalhousie-4n-5d" className="text-gray-400 hover:text-orange-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  Dharamshala Dalhousie 4N/5D
                </Link>
              </li>
              <li>
                <Link to="/packages/dharamshala-3n-4d" className="text-gray-400 hover:text-orange-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  Dharamshala 3N/4D
                </Link>
              </li>
              <li>
                <Link to="/packages/dalhousie-3n-4d" className="text-gray-400 hover:text-orange-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  Dalhousie Khajjiar 3N/4D
                </Link>
              </li>
              <li>
                <Link to="/packages/amritsar-2n-3d" className="text-gray-400 hover:text-orange-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  Amritsar Golden Temple
                </Link>
              </li>
              <li>
                <Link to="/packages/shimla-manali-honeymoon" className="text-gray-400 hover:text-orange-400 transition-colors text-sm font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  Shimla Manali Honeymoon
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white relative inline-block">
              Contact Us
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-green-500 to-orange-500 rounded-full"></span>
            </h3>
            <ul className="space-y-4 mt-4">
              <li className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/20">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Call Us 24×7</p>
                  <a href="tel:9816154248" className="text-white hover:text-green-400 transition-colors font-bold">
                    9816154248
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/20">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Email Us</p>
                  <a href="mailto:dharamshaladalhousietour@gmail.com" className="text-white hover:text-orange-400 transition-colors text-sm">
                    dharamshaladalhousietour@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Location</p>
                  <p className="text-gray-300 text-sm">
                    Dharamshala, Himachal Pradesh, India
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <a 
                  href="https://www.instagram.com/dharamshaladalhousietour?igsh=eWYyczdxOXBzZGIz" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 group"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-pink-500/20 group-hover:scale-110 transition-transform">
                    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Follow Us</p>
                    <p className="text-white group-hover:text-pink-400 transition-colors text-sm font-semibold">
                      @dharamshaladalhousietour
                    </p>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-gray-500 text-sm">
                © {currentYear} Dharamshala Dalhousie Tour Package. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm mt-1">
                A unit of{' '}
                <a 
                  href="https://prettyplanettravels.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:text-orange-300 font-semibold transition-colors"
                >
                  Pretty Planet Travels and Events
                </a>
              </p>
            </div>
            <p className="text-gray-500 text-sm flex items-center gap-2">
              Crafted with <span className="text-red-500">❤️</span> for Himachal travelers
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
